// Copyright (c) Ergoss Logiciels - All rights reserved - Proprietary and confidential
import { Injectable, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import type { SQLiteDBConnection } from '@capacitor-community/sqlite';
import { CapacitorSQLite, SQLiteConnection } from '@capacitor-community/sqlite';
import { LoggerService } from '@core/services/logger.service';

const DB_NAME = 'pwi-offline';
const SCHEMA_VERSION = 1;

// v1: the read cache, partitioned PER PILOT from the very first table — the
// vault structure precedes the key policy (conception notes §2/§4), so the
// later per-pilot key upgrade is a re-keying, never a data migration. The
// pending-action queue table arrives with plan phase C and, once it exists,
// must survive every subsequent migration (P3: no silent loss).
const SCHEMA_V1: string[] = [
  `CREATE TABLE IF NOT EXISTS read_cache (
    pilot_id TEXT NOT NULL,
    cache_key TEXT NOT NULL,
    body TEXT NOT NULL,
    captured_at INTEGER NOT NULL,
    PRIMARY KEY (pilot_id, cache_key)
  );`,
];

/**
 * B1 — the offline store: one SQLCipher-encrypted SQLite database in the app
 * container (native only; the web build has no offline requirement and
 * resolves to "unavailable" by design). This service owns the connection,
 * the encryption secret handshake (generated once per install, held in the
 * iOS Keychain by the plugin), and the schema migrations. It exposes a thin
 * read-cache API — the seam the B2 read-through interceptor plugs into, the
 * same swap-inside-the-seam move as NetworkService (PR 11032) and the spike's
 * in-memory prototype.
 */
@Injectable({ providedIn: 'root' })
export class OfflineStore {
  private readonly logger = inject(LoggerService);
  private db: SQLiteDBConnection | null = null;
  private ready: Promise<boolean> | null = null;

  /**
   * Opens (lazily, once) the encrypted database. Resolves `false` on web and
   * on any initialization failure — callers must degrade gracefully, never
   * throw: offline mode is an enhancement, not a boot dependency.
   */
  ensureReady(): Promise<boolean> {
    this.ready ??= this.initialize();
    return this.ready;
  }

  /** Read-cache lookup for one pilot. Null on miss or when the store is unavailable. */
  async getRead(pilotId: string, cacheKey: string): Promise<{ body: unknown; capturedAt: number } | null> {
    if (!(await this.ensureReady()) || !this.db) return null;
    const res = await this.db.query('SELECT body, captured_at FROM read_cache WHERE pilot_id = ? AND cache_key = ?;', [
      pilotId,
      cacheKey,
    ]);
    const row = res.values?.[0] as { body: string; captured_at: number } | undefined;
    if (!row) return null;
    try {
      return { body: JSON.parse(row.body), capturedAt: row.captured_at };
    } catch {
      // A row that no longer parses is corrupt cache, not user data — drop it.
      await this.db.run('DELETE FROM read_cache WHERE pilot_id = ? AND cache_key = ?;', [pilotId, cacheKey]);
      return null;
    }
  }

  /** Read-cache upsert for one pilot. A no-op when the store is unavailable. */
  async setRead(pilotId: string, cacheKey: string, body: unknown): Promise<void> {
    if (!(await this.ensureReady()) || !this.db) return;
    await this.db.run(
      'INSERT OR REPLACE INTO read_cache (pilot_id, cache_key, body, captured_at) VALUES (?, ?, ?, ?);',
      [pilotId, cacheKey, JSON.stringify(body), Date.now()],
    );
  }

  /**
   * Rows whose cache_key matches the SQL LIKE pattern, for one pilot. Corrupt
   * rows (unparseable JSON) are skipped, not returned. Used by the window-sync
   * reconciliation (B4): list snapshots define the retention window.
   */
  async getReadsMatching(
    pilotId: string,
    keyLike: string,
  ): Promise<{ cacheKey: string; body: unknown; capturedAt: number }[]> {
    if (!(await this.ensureReady()) || !this.db) return [];
    const res = await this.db.query(
      'SELECT cache_key, body, captured_at FROM read_cache WHERE pilot_id = ? AND cache_key LIKE ?;',
      [pilotId, keyLike],
    );
    const rows = (res.values ?? []) as { cache_key: string; body: string; captured_at: number }[];
    const out: { cacheKey: string; body: unknown; capturedAt: number }[] = [];
    for (const row of rows) {
      try {
        out.push({ cacheKey: row.cache_key, body: JSON.parse(row.body), capturedAt: row.captured_at });
      } catch {
        // Corrupt cache row — reconciliation must not choke on it.
      }
    }
    return out;
  }

  /** Removes one cached read. A no-op when the store is unavailable. */
  async deleteRead(pilotId: string, cacheKey: string): Promise<void> {
    if (!(await this.ensureReady()) || !this.db) return;
    await this.db.run('DELETE FROM read_cache WHERE pilot_id = ? AND cache_key = ?;', [pilotId, cacheKey]);
  }

  /** Removes matching reads captured before the cutoff (retention TTL sweep). */
  async deleteReadsOlderThan(pilotId: string, keyLike: string, beforeEpoch: number): Promise<void> {
    if (!(await this.ensureReady()) || !this.db) return;
    await this.db.run('DELETE FROM read_cache WHERE pilot_id = ? AND cache_key LIKE ? AND captured_at < ?;', [
      pilotId,
      keyLike,
      beforeEpoch,
    ]);
  }

  /**
   * Dev diagnostics for the bed pass: a `_diag/boot` row whose body is a boot
   * counter — it incrementing across app relaunches IS the persistence proof,
   * readable straight from the console. Harmless data; costs one row.
   */
  async logBootDiagnostics(): Promise<void> {
    const previous = await this.getRead('_diag', 'boot');
    const bootCount = (typeof previous?.body === 'number' ? previous.body : 0) + 1;
    await this.setRead('_diag', 'boot', bootCount);
    this.logger.info('OfflineStore', `ready — encrypted, schema v${SCHEMA_VERSION}, boot #${bootCount}`);
  }

  private async initialize(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      this.logger.debug('OfflineStore', 'web build — offline store disabled by design');
      return false;
    }
    try {
      const sqlite = new SQLiteConnection(CapacitorSQLite);

      // Encryption secret: generated once per install, then held in the iOS
      // Keychain BY THE PLUGIN — the app never stores it anywhere else, and it
      // never leaves the device. Per-install (not per-pilot) for now — see the
      // class comment and conception notes §4 (gated on the grace-access call).
      const { result: hasSecret } = await sqlite.isSecretStored();
      if (!hasSecret) {
        await sqlite.setEncryptionSecret(this.generateSecret());
        this.logger.info('OfflineStore', 'encryption secret generated and stored in the Keychain');
      }

      await sqlite.addUpgradeStatement(DB_NAME, [{ toVersion: 1, statements: SCHEMA_V1 }]);

      // The native connection registry outlives the JS context: the OAuth
      // round-trip RELOADS the webview in the same native process, so the
      // post-login context finds the pre-login context's connection already
      // registered — createConnection would throw "already exists" and the
      // store would be dead for the whole logged-in session (bed catch,
      // 2026-08-18). Canonical plugin pattern: retrieve when it exists.
      const consistent = (await sqlite.checkConnectionsConsistency()).result === true;
      const exists = (await sqlite.isConnection(DB_NAME, false)).result === true;
      this.db =
        consistent && exists
          ? await sqlite.retrieveConnection(DB_NAME, false)
          : await sqlite.createConnection(DB_NAME, true, 'secret', SCHEMA_VERSION, false);
      const isOpen = (await this.db.isDBOpen()).result === true;
      if (!isOpen) {
        await this.db.open();
      }
      return true;
    } catch (err) {
      this.logger.error('OfflineStore', 'initialization failed — offline store unavailable', err);
      this.db = null;
      return false;
    }
  }

  private generateSecret(): string {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    return btoa(String.fromCharCode(...bytes));
  }
}
